import type { AdvancedFilters, DashboardMode, DrilldownFilterPresetDto } from '@nexus/contracts';
import { DrilldownFilterPresetPayloadSchema, hasCrossLevelOr } from '@nexus/contracts';
import { presetGlobalMatches } from '@/features/dashboard/lib/filter-preset-match';
import { useDashboardFiltersStore } from '@/features/dashboard/stores/dashboardFilters.store';
import { withSnapshotDates } from '@/features/dashboard/lib/explore-date-presets';
import type { CompetitorFilters, MyBrandFilters } from '@/features/dashboard/types/dashboard.types';
import {
  type DrilldownFiltersState,
  normalizeLegacyGroupbyRules,
  useDrilldownFiltersStore,
} from '@/features/drilldown/stores/drilldownFilters.store';

function competitorsPageDraftKey(state: DrilldownFiltersState): string {
  return JSON.stringify({
    objectType: state.objectType,
    effectiveStatus: state.effectiveStatus,
    segment1: state.segment1,
    segment2: state.segment2,
    segment3: state.segment3,
    advancedFilters: state.advancedFilters,
    competitorSortMode: state.competitorSortMode,
    competitorSortDirection: state.competitorSortDirection,
    viewMode: state.viewMode,
  });
}

function myBrandPageDraftKey(state: DrilldownFiltersState): string {
  return JSON.stringify({
    myBrandObjectType: state.myBrandObjectType,
    myBrandEffectiveStatus: state.myBrandEffectiveStatus,
    audienceSegment: state.audienceSegment,
    device: state.device,
    aspectRatio: state.aspectRatio,
    myBrandSegment1: state.myBrandSegment1,
    myBrandSegment2: state.myBrandSegment2,
    myBrandSegment3: state.myBrandSegment3,
    myBrandAdvancedFilters: state.myBrandAdvancedFilters,
    myBrandKpis: state.myBrandKpis,
    myBrandSortMode: state.myBrandSortMode,
    myBrandSortDirection: state.myBrandSortDirection,
    myBrandHiddenColumns: state.myBrandHiddenColumns,
    viewMode: state.viewMode,
  });
}

export function selectDrilldownDraftKey(
  state: DrilldownFiltersState,
  mode: DashboardMode,
): string {
  return mode === 'my-brand' ? myBrandPageDraftKey(state) : competitorsPageDraftKey(state);
}

function presetAdvancedFilters(preset: DrilldownFilterPresetDto): AdvancedFilters {
  const { filters } = preset;
  if (filters.mode === 'my-brand') {
    const { page } = filters;
    return normalizeLegacyGroupbyRules(page.myBrandAdvancedFilters, [
      page.myBrandSegment1,
      page.myBrandSegment2,
      page.myBrandSegment3,
    ]);
  }
  const { page } = filters;
  return normalizeLegacyGroupbyRules(page.advancedFilters, [page.segment1, page.segment2, page.segment3]);
}

export function drilldownPresetPageKey(
  preset: DrilldownFilterPresetDto,
): string {
  const { filters } = preset;
  const advancedFilters = presetAdvancedFilters(preset);
  if (filters.mode === 'my-brand') {
    const page = filters.page;
    // Same key order as myBrandPageDraftKey. Rebuilding (instead of stringifying the
    // stored page) keeps a preset saved before hidden columns still match, and ignores
    // whatever order the payload happened to be parsed in. Advanced filters are the
    // normalized form so a legacy GroupbyN rule matches the live draft after load.
    return JSON.stringify({
      myBrandObjectType: page.myBrandObjectType,
      myBrandEffectiveStatus: page.myBrandEffectiveStatus,
      audienceSegment: page.audienceSegment,
      device: page.device,
      aspectRatio: page.aspectRatio,
      myBrandSegment1: page.myBrandSegment1,
      myBrandSegment2: page.myBrandSegment2,
      myBrandSegment3: page.myBrandSegment3,
      myBrandAdvancedFilters: advancedFilters,
      myBrandKpis: page.myBrandKpis,
      myBrandSortMode: page.myBrandSortMode,
      myBrandSortDirection: page.myBrandSortDirection,
      myBrandHiddenColumns: page.myBrandHiddenColumns ?? [],
      viewMode: page.viewMode,
    });
  }
  return JSON.stringify({ ...filters.page, advancedFilters });
}

/** True when the preset's advanced filters join a dimension rule to a metric rule with Or. */
export function drilldownPresetHasCrossLevelOr(preset: DrilldownFilterPresetDto): boolean {
  return hasCrossLevelOr(presetAdvancedFilters(preset));
}

export function buildCurrentDrilldownPresetPayload(mode: DashboardMode) {
  const drilldown = useDrilldownFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();

  if (mode === 'my-brand') {
    return DrilldownFilterPresetPayloadSchema.parse({
      mode: 'my-brand',
      page: {
        myBrandObjectType: drilldown.appliedMyBrandObjectType,
        myBrandEffectiveStatus: drilldown.appliedMyBrandEffectiveStatus,
        audienceSegment: drilldown.appliedAudienceSegment,
        device: drilldown.appliedDevice,
        aspectRatio: drilldown.appliedAspectRatio,
        myBrandSegment1: drilldown.appliedMyBrandSegment1,
        myBrandSegment2: drilldown.appliedMyBrandSegment2,
        myBrandSegment3: drilldown.appliedMyBrandSegment3,
        myBrandAdvancedFilters: drilldown.appliedMyBrandAdvancedFilters,
        myBrandKpis: drilldown.myBrandKpis,
        myBrandHiddenColumns: drilldown.myBrandHiddenColumns,
        myBrandSortMode: drilldown.myBrandSortMode,
        myBrandSortDirection: drilldown.myBrandSortDirection,
        viewMode: drilldown.viewMode,
      },
      global: dashboard.appliedMyBrandFilters,
    });
  }

  return DrilldownFilterPresetPayloadSchema.parse({
    mode: 'competitors',
    page: {
      objectType: drilldown.appliedObjectType,
      effectiveStatus: drilldown.appliedEffectiveStatus,
      segment1: drilldown.appliedSegment1,
      segment2: drilldown.appliedSegment2,
      segment3: drilldown.appliedSegment3,
      advancedFilters: drilldown.appliedAdvancedFilters,
      competitorSortMode: drilldown.competitorSortMode,
      competitorSortDirection: drilldown.competitorSortDirection,
      viewMode: drilldown.viewMode,
    },
    global: dashboard.appliedCompetitorFilters,
  });
}

export function applyDrilldownFilterPreset(preset: DrilldownFilterPresetDto): void {
  const { filters } = preset;
  const dashboard = useDashboardFiltersStore.getState();

  useDrilldownFiltersStore.getState().loadPresetPageState(filters.mode, filters.page);

  if (filters.mode === 'my-brand') {
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'my-brand',
      competitorFilters: dashboard.appliedCompetitorFilters,
      myBrandFilters: withSnapshotDates(filters.global as MyBrandFilters),
    });
  } else {
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'competitors',
      competitorFilters: withSnapshotDates(filters.global as CompetitorFilters),
      myBrandFilters: dashboard.appliedMyBrandFilters,
    });
  }
  useDashboardFiltersStore.getState().applyFilters();
  useDashboardFiltersStore.getState().setMode(filters.mode);
}

export function drilldownPresetMatchesCurrentFilters(preset: DrilldownFilterPresetDto): boolean {
  const drilldown = useDrilldownFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const presetMode = preset.filters.mode;

  if (selectDrilldownDraftKey(drilldown, presetMode) !== drilldownPresetPageKey(preset)) {
    return false;
  }

  return presetGlobalMatches(
    presetMode,
    dashboard,
    preset.filters.global as CompetitorFilters | MyBrandFilters,
  );
}
